import React, { useState, useEffect } from "react";
import { Container, Row, Col, Form, Button } from "react-bootstrap";
import Swal from "sweetalert2";
import { useTranslation } from "react-i18next";

const ContactPage = () => {
  const { t } = useTranslation();

  const [formData, setFormData] = useState({
    name: "",
    phonenumber: "",
    message: "",
  });
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 767);

  // Get API base URL from environment variable
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 767);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // ✅ Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  // ✅ Validate and send message
  const handleSendMessage = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.phonenumber || !formData.message) {
      Swal.fire({
        icon: "error",
        title: t("Oops..."),
        text: t("Please fill out all fields!"),
        timer: 2000,
      });
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/messages`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(formData),
        }
      );

      if (response.ok) {
        Swal.fire({
          icon: "success",
          title: t("Success!"),
          text: t("Message sent successfully!"),
          timer: 2000,
        });

        setFormData({ name: "", phonenumber: "", message: "" });
      } else {
        Swal.fire({
          icon: "error",
          title: t("Oops..."),
          text: t("Failed to send message. Please try again."),
        });
      }
    } catch (error) {
      console.error("Error sending message:", error);
      Swal.fire({
        icon: "error",
        title: t("Oops..."),
        text: t("An error occurred. Please try again."),
      });
    }
  };

  return (
    <main className="contact-page" style={{ fontFamily: "'Hanuman', Arial, sans-serif" }}>
      {/* ✅ Header Section */}
      <section className="contact-section d-flex justify-content-center align-items-center">
        <Container
          fluid
          className="shopping-header contact-header d-flex justify-content-center align-items-center"
        >
          <div className="d-flex flex-column justify-content-center align-items-center mt-5 mt-md-0">
            <span className="text-white fs-5 mt-5 mt-md-0">{t("Home / Contact")}</span>
            <h1 className="mt-2 fs-custom text-white">{t("Contact")}</h1>
          </div>
        </Container>
      </section>

      {/* ✅ Contact Form + Map Section */}
      <section className="contact-info mt-5 mb-5">
        <Container>
          <Row className="g-4 p-2">
            {/* ✅ Form Section */}
            <Col
              lg={6}
              className="border border-secondary border-opacity-25 rounded-3 shadow-sm "
            >
              <Form
                className="p-3 p-md-4 d-flex flex-column justify-content-start align-items-start"
                onSubmit={handleSendMessage}
              >
                <Form.Label className="fs-3 fw-medium">
                  {t("Get In Touch")}
                </Form.Label>

                <Form.Group
                  className="my-3 d-flex justify-content-between gap-2 gap-md-4 w-100"
                  controlId="formBasicContact"
                >
                  <div className="w-100">
                    <Form.Label>{t("Phone Number")}</Form.Label>
                    <Form.Control
                      type="text"
                      name="phonenumber"
                      placeholder={t("Enter your phone number")}
                      value={formData.phonenumber}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="w-100">
                    <Form.Label>{t("Your Name")}</Form.Label>
                    <Form.Control
                      type="text"
                      name="name"
                      placeholder={t("Enter your name")}
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </Form.Group>

                <Form.Group
                  className="my-3 d-flex flex-column w-100"
                  controlId="formBasicMessage"
                >
                  <Form.Label>{t("Your Message")}</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    name="message"
                    placeholder={t("Write your message here...")}
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                  />
                </Form.Group>

                <Button
                  variant="primary"
                  type="submit"
                  className="w-100 my-3 btn-color border-0 btn-card-hover py-2"
                >
                  {t("Send Message")}
                </Button>
              </Form>
            </Col>
            {/* ✅ Map Section */}
            <Col
              lg={6}
              className="d-flex flex-column justify-content-center align-items-center py-0"
            >
              <div
                style={{
                  width: "100%",
                  height: isMobile ? "300px" : "500px",
                  borderRadius: "8px",
                  border: "1px solid #e0e0e0",
                  overflow: "hidden",
                  marginBottom: "0px",
                }}
              >
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3907.9406111103176!2d104.87865529999999!3d11.6275824!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x310953b392025787%3A0xa1dc87c0497dbddd!2sKroob%20Scent!5e0!3m2!1sen!2skh!4v1782751050800!5m2!1sen!2skh"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowfullscreen=""
                  loading="lazy"
                  referrerpolicy="strict-origin-when-cross-origin"
                  title="Google Map Location"
                ></iframe>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </main>
  );
};

export default ContactPage;